import {
  ProtocolError,
  crc32c,
  decodeTransportPacket,
  encodeTransportPacket,
  type TransportPacket,
} from '@spectrabus/protocol';

export const OPTICAL_FRAME_WIDTH = 120;
export const OPTICAL_FRAME_HEIGHT = 80;
export const OPTICAL_HEADER_BYTES = 12;

export interface OpticalCellField {
  readonly width: number;
  readonly height: number;
  readonly profile: 'O1-BINARY';
  readonly cells: Uint8Array;
}

export interface OpticalFrame extends OpticalCellField {
  readonly sequence: number;
  readonly dataCellIndices: Uint32Array;
}

export interface SampledOpticalFrame extends OpticalCellField {
  readonly sequence?: never;
}

export type DecodableOpticalFrame = OpticalFrame | SampledOpticalFrame;

export interface DecodedOpticalPayload {
  readonly sequence: number;
  readonly payload: Uint8Array;
}

export interface OpticalFrameGeometry {
  readonly referenceCells: Uint8Array;
  readonly reservedCellIndices: Uint32Array;
  readonly dataCellIndices: Uint32Array;
}

const QUIET_ZONE = 3;
const FINDER_SIZE = 11;
const OPTICAL_MAGIC = 0x4f505431;
const OPTICAL_HEADER_BITS = OPTICAL_HEADER_BYTES * 8;

function cellIndex(x: number, y: number): number {
  return y * OPTICAL_FRAME_WIDTH + x;
}

function drawFinder(
  cells: Uint8Array,
  reserved: Uint8Array,
  originX: number,
  originY: number,
  orientation: number,
): void {
  for (let y = 0; y < FINDER_SIZE; y += 1) {
    for (let x = 0; x < FINDER_SIZE; x += 1) {
      const outer = x === 0 || y === 0 || x === FINDER_SIZE - 1 || y === FINDER_SIZE - 1;
      const middle = x >= 3 && x <= 7 && y >= 3 && y <= 7;
      const notch = x === orientation && y === 1;
      const index = cellIndex(originX + x, originY + y);
      cells[index] = outer || middle || notch ? 1 : 0;
      reserved[index] = 1;
    }
  }
}

function initialiseFrameGeometry(): {
  cells: Uint8Array;
  reserved: Uint8Array;
  dataCellIndices: Uint32Array;
} {
  const cells = new Uint8Array(OPTICAL_FRAME_WIDTH * OPTICAL_FRAME_HEIGHT);
  const reserved = new Uint8Array(cells.length);

  for (let y = 0; y < OPTICAL_FRAME_HEIGHT; y += 1) {
    for (let x = 0; x < OPTICAL_FRAME_WIDTH; x += 1) {
      if (
        x < QUIET_ZONE ||
        y < QUIET_ZONE ||
        x >= OPTICAL_FRAME_WIDTH - QUIET_ZONE ||
        y >= OPTICAL_FRAME_HEIGHT - QUIET_ZONE
      ) {
        reserved[cellIndex(x, y)] = 1;
      }
    }
  }

  drawFinder(cells, reserved, 4, 4, 2);
  drawFinder(cells, reserved, OPTICAL_FRAME_WIDTH - 15, 4, 8);
  drawFinder(cells, reserved, 4, OPTICAL_FRAME_HEIGHT - 15, 2);
  drawFinder(
    cells,
    reserved,
    OPTICAL_FRAME_WIDTH - 15,
    OPTICAL_FRAME_HEIGHT - 15,
    8,
  );

  for (let x = 16; x < OPTICAL_FRAME_WIDTH - 16; x += 1) {
    const index = cellIndex(x, 15);
    cells[index] = x % 2;
    reserved[index] = 1;
  }
  for (let y = 16; y < OPTICAL_FRAME_HEIGHT - 16; y += 1) {
    const index = cellIndex(15, y);
    cells[index] = y % 2;
    reserved[index] = 1;
  }

  const pilotValues = [0, 1, 2, 3] as const;
  for (let pilot = 0; pilot < pilotValues.length; pilot += 1) {
    for (let y = 0; y < 3; y += 1) {
      for (let x = 0; x < 5; x += 1) {
        const index = cellIndex(31 + pilot * 12 + x, OPTICAL_FRAME_HEIGHT - 9 + y);
        cells[index] = pilotValues[pilot] ?? 0;
        reserved[index] = 1;
      }
    }
  }

  const indices: number[] = [];
  for (let y = QUIET_ZONE; y < OPTICAL_FRAME_HEIGHT - QUIET_ZONE; y += 1) {
    const leftToRight = y % 2 === 0;
    for (let step = 0; step < OPTICAL_FRAME_WIDTH - QUIET_ZONE * 2; step += 1) {
      const x = leftToRight
        ? QUIET_ZONE + step
        : OPTICAL_FRAME_WIDTH - QUIET_ZONE - 1 - step;
      const index = cellIndex(x, y);
      if (reserved[index] === 0) indices.push(index);
    }
  }
  return { cells, reserved, dataCellIndices: Uint32Array.from(indices) };
}

export function getOpticalFrameGeometry(): OpticalFrameGeometry {
  const geometry = initialiseFrameGeometry();
  const reservedCellIndices: number[] = [];
  for (let index = 0; index < geometry.reserved.length; index += 1) {
    if (geometry.reserved[index] === 1) reservedCellIndices.push(index);
  }
  return {
    referenceCells: geometry.cells,
    reservedCellIndices: Uint32Array.from(reservedCellIndices),
    dataCellIndices: geometry.dataCellIndices,
  };
}

export function createSampledOpticalFrame(cells: Uint8Array): SampledOpticalFrame {
  const expectedLength = OPTICAL_FRAME_WIDTH * OPTICAL_FRAME_HEIGHT;
  if (cells.length !== expectedLength) {
    throw new ProtocolError(
      'OPTICAL_GEOMETRY',
      `Sampled O1 field contains ${cells.length} cells; expected ${expectedLength}.`,
    );
  }
  return {
    width: OPTICAL_FRAME_WIDTH,
    height: OPTICAL_FRAME_HEIGHT,
    profile: 'O1-BINARY',
    cells: cells.slice(),
  };
}

function whiteningBit(sequence: number, index: number): number {
  let value = (sequence ^ Math.imul(index + 1, 0x9e3779b1)) >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value & 1;
}

function uint32(value: number): Uint8Array {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value);
  return bytes;
}

export function encodeOpticalPayload(payload: Uint8Array, sequence: number): OpticalFrame {
  if (!Number.isInteger(sequence) || sequence < 0 || sequence > 0xffff_ffff) {
    throw new ProtocolError('OPTICAL_SEQUENCE', 'Optical frame sequence must be an unsigned 32-bit integer.');
  }
  const prefix = new Uint8Array(OPTICAL_HEADER_BYTES);
  const prefixView = new DataView(prefix.buffer);
  prefixView.setUint32(0, OPTICAL_MAGIC);
  prefixView.setUint32(4, sequence);
  prefixView.setUint32(8, payload.length);
  const messageWithoutCrc = new Uint8Array(prefix.length + payload.length);
  messageWithoutCrc.set(prefix);
  messageWithoutCrc.set(payload, prefix.length);
  const message = new Uint8Array(messageWithoutCrc.length + 4);
  message.set(messageWithoutCrc);
  message.set(uint32(crc32c(messageWithoutCrc)), messageWithoutCrc.length);

  const geometry = initialiseFrameGeometry();
  if (message.length * 8 > geometry.dataCellIndices.length) {
    throw new ProtocolError('OPTICAL_CAPACITY', 'Transport packet exceeds the O1 raster capacity.');
  }

  for (let bitIndex = 0; bitIndex < message.length * 8; bitIndex += 1) {
    const byte = message[Math.floor(bitIndex / 8)] ?? 0;
    const bit = (byte >>> (7 - (bitIndex % 8))) & 1;
    const index = geometry.dataCellIndices[bitIndex];
    if (index !== undefined) {
      const mask = bitIndex < OPTICAL_HEADER_BITS ? 0 : whiteningBit(sequence, bitIndex);
      geometry.cells[index] = bit ^ mask;
    }
  }

  return {
    width: OPTICAL_FRAME_WIDTH,
    height: OPTICAL_FRAME_HEIGHT,
    sequence,
    profile: 'O1-BINARY',
    cells: geometry.cells,
    dataCellIndices: geometry.dataCellIndices,
  };
}

export function encodeOpticalFrame(packet: TransportPacket): OpticalFrame {
  return encodeOpticalPayload(encodeTransportPacket(packet), packet.sequence);
}

function extractBytes(
  cells: Uint8Array,
  dataCellIndices: Uint32Array,
  byteLength: number,
  sequence?: number,
): Uint8Array {
  if (byteLength * 8 > dataCellIndices.length) {
    throw new ProtocolError('OPTICAL_LENGTH', 'Declared optical payload exceeds frame capacity.');
  }
  const bytes = new Uint8Array(byteLength);
  for (let bitIndex = 0; bitIndex < byteLength * 8; bitIndex += 1) {
    const cell = cells[dataCellIndices[bitIndex] ?? 0] ?? 0;
    let mask = 0;
    if (bitIndex >= OPTICAL_HEADER_BITS) {
      if (sequence === undefined) {
        throw new ProtocolError(
          'OPTICAL_SEQUENCE',
          'Optical payload whitening requires the recovered header sequence.',
        );
      }
      mask = whiteningBit(sequence, bitIndex);
    }
    const bit = (cell & 1) ^ mask;
    const byteIndex = Math.floor(bitIndex / 8);
    bytes[byteIndex] = (bytes[byteIndex] ?? 0) | (bit << (7 - (bitIndex % 8)));
  }
  return bytes;
}

export function decodeOpticalPayload(frame: DecodableOpticalFrame): DecodedOpticalPayload {
  if (frame.width !== OPTICAL_FRAME_WIDTH || frame.height !== OPTICAL_FRAME_HEIGHT) {
    throw new ProtocolError('OPTICAL_GEOMETRY', 'Optical frame dimensions do not match O1.');
  }
  if (frame.cells.length !== OPTICAL_FRAME_WIDTH * OPTICAL_FRAME_HEIGHT) {
    throw new ProtocolError('OPTICAL_GEOMETRY', 'Optical frame cell count does not match O1.');
  }

  const geometry = initialiseFrameGeometry();
  const prefix = extractBytes(
    frame.cells,
    geometry.dataCellIndices,
    OPTICAL_HEADER_BYTES,
  );
  const view = new DataView(prefix.buffer);
  if (view.getUint32(0) !== OPTICAL_MAGIC) {
    throw new ProtocolError('OPTICAL_HEADER', 'Optical frame header failed its magic check.');
  }
  const sequence = view.getUint32(4);
  if ('sequence' in frame && frame.sequence !== undefined && sequence !== frame.sequence) {
    throw new ProtocolError('OPTICAL_SEQUENCE', 'Optical frame sequence is inconsistent.');
  }
  const payloadLength = view.getUint32(8);
  const message = extractBytes(
    frame.cells,
    geometry.dataCellIndices,
    OPTICAL_HEADER_BYTES + payloadLength + 4,
    sequence,
  );
  const expectedCrc = new DataView(
    message.buffer,
    message.byteOffset + message.length - 4,
    4,
  ).getUint32(0);
  const actualCrc = crc32c(message.subarray(0, message.length - 4));
  if (actualCrc !== expectedCrc) {
    throw new ProtocolError('OPTICAL_CRC', 'Optical raster CRC32C verification failed.');
  }
  return {
    sequence,
    payload: message.slice(OPTICAL_HEADER_BYTES, message.length - 4),
  };
}

export function decodeOpticalFrame(frame: DecodableOpticalFrame): TransportPacket {
  return decodeTransportPacket(decodeOpticalPayload(frame).payload);
}

export function damageOpticalFrame(
  frame: OpticalFrame,
  fraction: number,
  seed: number,
): OpticalFrame {
  const cells = frame.cells.slice();
  const count = Math.floor(frame.dataCellIndices.length * Math.max(0, Math.min(1, fraction)));
  let state = seed >>> 0;
  for (let index = 0; index < count; index += 1) {
    // Each xorshift step is forced back to unsigned 32-bit; the << operator
    // yields a signed Int32, and a negative state would index the cell table
    // with a negative modulus and silently skip the flip.
    state = (state ^ (state << 13)) >>> 0;
    state = (state ^ (state >>> 17)) >>> 0;
    state = (state ^ (state << 5)) >>> 0;
    const position = frame.dataCellIndices[state % frame.dataCellIndices.length];
    if (position !== undefined) cells[position] = (cells[position] ?? 0) ^ 1;
  }
  return { ...frame, cells };
}
